import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Image, Line, Circle, Rect, Arrow, Text as KonvaText } from 'react-konva';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import { 
  Circle as CircleIcon,
  ArrowRight,
  Type,
  Minus,
  Square,
  Pen,
  Undo,
  Redo,
  Trash2,
  Save,
  ChevronLeft,
  Palette,
  Loader2
} from 'lucide-react';
import type { CaptureMetadata } from './SatelliteViewCapture';
import Konva from 'konva';

interface SatelliteAnnotatorProps {
  imageData: string;
  metadata: CaptureMetadata;
  wellName: string;
  onSave: (annotatedImageData: string, hasAnnotations: boolean) => void;
  onCancel: () => void;
}

type DrawingTool = 'select' | 'circle' | 'arrow' | 'text' | 'line' | 'rectangle' | 'freehand';

interface Annotation {
  id: string;
  tool: DrawingTool;
  color: string;
  points?: number[]; // For lines and freehand
  x?: number;
  y?: number;
  radius?: number; // For circles
  width?: number;
  height?: number; // For rectangles
  text?: string; // For text annotations
}

const COLORS = [
  { name: 'Red', value: '#EF4444' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Black', value: '#000000' }
];

export default function SatelliteAnnotator({
  imageData,
  metadata,
  wellName,
  onSave,
  onCancel
}: SatelliteAnnotatorProps) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [activeTool, setActiveTool] = useState<DrawingTool>('select');
  const [selectedColor, setSelectedColor] = useState('#EF4444');
  const [isDrawing, setIsDrawing] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [history, setHistory] = useState<Annotation[][]>([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [textEditorPosition, setTextEditorPosition] = useState<{x: number, y: number} | null>(null);
  const [textEditorValue, setTextEditorValue] = useState('');
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  
  const stageRef = useRef<Konva.Stage>(null);
  const isDrawingRef = useRef(false);
  const currentAnnotationRef = useRef<Annotation | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Canvas dimensions
  const containerWidth = Math.min(window.innerWidth - 40, 1200);
  const containerHeight = Math.min(window.innerHeight - 250, 900);
  const canvasWidth = containerWidth;
  const canvasHeight = containerHeight;

  // Load image from data URL
  useEffect(() => {
    if (!imageData) {
      console.error('❌ No image data provided');
      setLoadError('No image data to annotate');
      toast.error('No image data to annotate');
      return;
    }

    const img = new window.Image();
    
    img.onload = () => {
      setImage(img);
      setImageLoaded(true);
      setLoadError(null);
      
      // Initialize history with empty state
      setHistory([[]]);
      setHistoryStep(0);
    };

    img.onerror = (error) => {
      console.error('❌ Failed to load satellite image:', error);
      setLoadError('Failed to load image for annotation');
      toast.error('Failed to load image');
    };

    img.src = imageData;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageData]);

  // Save state to history when annotations change
  useEffect(() => {
    if (!imageLoaded) return;
    
    // Only save if annotations actually changed
    const lastState = history[historyStep];
    if (JSON.stringify(lastState) === JSON.stringify(annotations)) {
      return;
    }

    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push([...annotations]);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  }, [annotations]);

  // Handle tool changes
  const handleToolChange = (tool: DrawingTool) => {
    setActiveTool(tool);
    setShowColorPicker(false);
  };

  // Handle color changes
  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    setShowColorPicker(false);
  };

  // Undo
  const handleUndo = () => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
      setAnnotations([...history[historyStep - 1]]);
    }
  };

  // Redo
  const handleRedo = () => {
    if (historyStep < history.length - 1) {
      setHistoryStep(historyStep + 1);
      setAnnotations([...history[historyStep + 1]]);
    }
  };

  // Clear all annotations
  const handleClear = () => {
    if (annotations.length === 0) {
      toast.info('No annotations to clear');
      return;
    }
    
    if (window.confirm('Clear all annotations?')) {
      setAnnotations([]);
      toast.success('Annotations cleared');
    }
  };

  // Mouse/Touch down - start drawing
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (activeTool === 'select') return;

    isDrawingRef.current = true;
    setIsDrawing(true);

    const stage = e.target.getStage();
    if (!stage) return;

    const pointerPosition = stage.getPointerPosition();
    if (!pointerPosition) return;

    const { x, y } = pointerPosition;
    const id = `annotation-${Date.now()}`;

    let newAnnotation: Annotation | null = null;

    switch (activeTool) {
      case 'freehand':
        newAnnotation = {
          id,
          tool: 'freehand',
          color: selectedColor,
          points: [x, y]
        };
        break;

      case 'line':
        newAnnotation = {
          id,
          tool: 'line',
          color: selectedColor,
          points: [x, y, x, y]
        };
        break;

      case 'arrow':
        newAnnotation = {
          id,
          tool: 'arrow',
          color: selectedColor,
          points: [x, y, x, y]
        };
        break;

      case 'circle':
        newAnnotation = {
          id,
          tool: 'circle',
          color: selectedColor,
          x,
          y,
          radius: 1
        };
        break;

      case 'rectangle':
        newAnnotation = {
          id,
          tool: 'rectangle',
          color: selectedColor,
          x,
          y,
          width: 1,
          height: 1
        };
        break;

      case 'text':
        // Show inline text editor at click position
        setTextEditorPosition({ x, y });
        setTextEditorValue('');
        setIsEditingText(true);
        return; // Don't create annotation yet - wait for user to type
    }

    if (newAnnotation) {
      currentAnnotationRef.current = newAnnotation;
      setAnnotations([...annotations, newAnnotation]);
    }
  };

  // Mouse/Touch move - continue drawing
  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (!isDrawingRef.current || !currentAnnotationRef.current) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const pointerPosition = stage.getPointerPosition();
    if (!pointerPosition) return;

    const { x, y } = pointerPosition;
    const current = currentAnnotationRef.current;

    // Update the current annotation based on tool
    let updatedAnnotation: Annotation = { ...current };

    switch (current.tool) {
      case 'freehand':
        updatedAnnotation.points = [...(current.points || []), x, y];
        break;

      case 'line':
      case 'arrow':
        if (current.points && current.points.length === 4) {
          updatedAnnotation.points = [current.points[0], current.points[1], x, y];
        }
        break;

      case 'circle':
        const dx = x - (current.x || 0);
        const dy = y - (current.y || 0);
        updatedAnnotation.radius = Math.sqrt(dx * dx + dy * dy);
        break;

      case 'rectangle':
        updatedAnnotation.width = x - (current.x || 0);
        updatedAnnotation.height = y - (current.y || 0);
        break;
    }

    currentAnnotationRef.current = updatedAnnotation;

    // Update last annotation in state
    setAnnotations(prev => {
      const updated = [...prev];
      updated[updated.length - 1] = updatedAnnotation;
      return updated;
    });
  };

  // Mouse/Touch up - finish drawing
  const handleMouseUp = () => {
    isDrawingRef.current = false;
    setIsDrawing(false);
    currentAnnotationRef.current = null;
  };

  // Complete text editing - create draggable Konva Text
  const completeTextEditing = () => {
    if (!textEditorValue.trim() || !textEditorPosition) {
      setIsEditingText(false);
      setTextEditorPosition(null);
      setTextEditorValue('');
      return;
    }

    const id = `annotation-${Date.now()}`;
    const newAnnotation: Annotation = {
      id,
      tool: 'text',
      color: selectedColor,
      x: textEditorPosition.x,
      y: textEditorPosition.y,
      text: textEditorValue
    };

    setAnnotations([...annotations, newAnnotation]);
    setIsEditingText(false);
    setTextEditorPosition(null);
    setTextEditorValue('');
    toast.success('Text added - drag to reposition');
  };

  // Handle text drag end - update position
  const handleTextDragEnd = (annotationId: string, e: Konva.KonvaEventObject<DragEvent>) => {
    // Get the actual final position from the target
    const node = e.target;
    const newX = node.x();
    const newY = node.y();
    
    
    // Update the annotation with the final position
    setAnnotations(prev => 
      prev.map(ann => 
        ann.id === annotationId 
          ? { ...ann, x: newX, y: newY }
          : ann
      )
    );
    
    // DO NOT reset position - let Konva handle it naturally to prevent drift
  };

  // Save annotated image
  const handleSave = async () => {
    if (!stageRef.current) {
      toast.error('Canvas not ready');
      return;
    }

    setIsSaving(true);

    try {
      // Export stage to data URL (PNG)
      const dataUrl = stageRef.current.toDataURL({
        mimeType: 'image/png',
        quality: 1,
        pixelRatio: 2 // Higher resolution
      });


      onSave(dataUrl, annotations.length > 0);
      toast.success('Satellite view saved successfully!');
    } catch (error) {
      console.error('❌ Error saving annotated image:', error);
      toast.error('Failed to save annotated image');
    } finally {
      setIsSaving(false);
    }
  };

  // Render annotations
  const renderAnnotations = () => {
    return annotations.map((annotation) => {
      switch (annotation.tool) {
        case 'freehand':
        case 'line':
          return (
            <Line
              key={annotation.id}
              points={annotation.points}
              stroke={annotation.color}
              strokeWidth={annotation.tool === 'freehand' ? 3 : 4}
              tension={annotation.tool === 'freehand' ? 0.5 : 0}
              lineCap="round"
              lineJoin="round"
            />
          );

        case 'arrow':
          return (
            <Arrow
              key={annotation.id}
              points={annotation.points}
              stroke={annotation.color}
              fill={annotation.color}
              strokeWidth={4}
              pointerLength={15}
              pointerWidth={15}
            />
          );

        case 'circle':
          return (
            <Circle
              key={annotation.id}
              x={annotation.x}
              y={annotation.y}
              radius={annotation.radius}
              stroke={annotation.color}
              strokeWidth={4}
              fill="transparent"
            />
          );

        case 'rectangle':
          return (
            <Rect
              key={annotation.id}
              x={annotation.x}
              y={annotation.y}
              width={annotation.width}
              height={annotation.height}
              stroke={annotation.color}
              strokeWidth={4}
              fill="transparent"
            />
          );

        case 'text':
          return (
            <KonvaText
              key={annotation.id}
              x={annotation.x}
              y={annotation.y}
              text={annotation.text}
              fontSize={24}
              fontFamily="Arial"
              fontStyle="bold"
              fill={annotation.color}
              stroke={annotation.id === selectedAnnotationId ? '#FFD700' : (annotation.color === '#FFFFFF' ? '#000000' : undefined)}
              strokeWidth={annotation.id === selectedAnnotationId ? 3 : (annotation.color === '#FFFFFF' ? 1 : 0)}
              draggable
              onDragEnd={(e) => handleTextDragEnd(annotation.id, e)}
              onClick={() => setSelectedAnnotationId(annotation.id)}
              onTouchStart={() => setSelectedAnnotationId(annotation.id)}
            />
          );

        default:
          return null;
      }
    });
  };

  if (loadError) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Failed to Load Image</h3>
              <p className="text-gray-600 mb-4">{loadError}</p>
              <Button onClick={onCancel}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!imageLoaded) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 flex flex-col items-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
          <p className="text-lg font-medium">Loading satellite image...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-7xl max-h-[95vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b z-10 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Pen className="h-5 w-5 text-blue-600" />
                Annotate Satellite View
              </h2>
              <p className="text-sm text-gray-600">{wellName}</p>
            </div>
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>

          {/* Drawing Tools */}
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              size="sm"
              variant={activeTool === 'freehand' ? 'default' : 'outline'}
              onClick={() => handleToolChange('freehand')}
              disabled={isSaving}
            >
              <Pen className="h-4 w-4 mr-2" />
              Freehand
            </Button>
            <Button
              size="sm"
              variant={activeTool === 'line' ? 'default' : 'outline'}
              onClick={() => handleToolChange('line')}
              disabled={isSaving}
            >
              <Minus className="h-4 w-4 mr-2" />
              Line
            </Button>
            <Button
              size="sm"
              variant={activeTool === 'arrow' ? 'default' : 'outline'}
              onClick={() => handleToolChange('arrow')}
              disabled={isSaving}
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Arrow
            </Button>
            <Button
              size="sm"
              variant={activeTool === 'circle' ? 'default' : 'outline'}
              onClick={() => handleToolChange('circle')}
              disabled={isSaving}
            >
              <CircleIcon className="h-4 w-4 mr-2" />
              Circle
            </Button>
            <Button
              size="sm"
              variant={activeTool === 'rectangle' ? 'default' : 'outline'}
              onClick={() => handleToolChange('rectangle')}
              disabled={isSaving}
            >
              <Square className="h-4 w-4 mr-2" />
              Rectangle
            </Button>
            <Button
              size="sm"
              variant={activeTool === 'text' ? 'default' : 'outline'}
              onClick={() => handleToolChange('text')}
              disabled={isSaving}
            >
              <Type className="h-4 w-4 mr-2" />
              Text
            </Button>

            <div className="h-6 w-px bg-gray-300 mx-2" />

            {/* Color Picker */}
            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowColorPicker(!showColorPicker)}
                disabled={isSaving}
              >
                <Palette className="h-4 w-4 mr-2" />
                <div 
                  className="w-4 h-4 rounded border border-gray-300" 
                  style={{ backgroundColor: selectedColor }}
                />
              </Button>
              {showColorPicker && (
                <div className="absolute top-full mt-2 bg-white border rounded-lg shadow-lg p-2 z-20">
                  <div className="grid grid-cols-3 gap-2">
                    {COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => handleColorChange(color.value)}
                        className="w-8 h-8 rounded border-2 hover:scale-110 transition-transform"
                        style={{ 
                          backgroundColor: color.value,
                          borderColor: selectedColor === color.value ? '#3B82F6' : '#D1D5DB'
                        }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="h-6 w-px bg-gray-300 mx-2" />

            {/* History Controls */}
            <Button
              size="sm"
              variant="outline"
              onClick={handleUndo}
              disabled={historyStep <= 0 || isSaving}
              title="Undo"
            >
              <Undo className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRedo}
              disabled={historyStep >= history.length - 1 || isSaving}
              title="Redo"
            >
              <Redo className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleClear}
              disabled={annotations.length === 0 || isSaving}
              title="Clear All"
            >
              <Trash2 className="h-4 w-4" />
            </Button>

            <div className="flex-1" />

            {/* Save Button */}
            <Button 
              onClick={handleSave}
              disabled={isSaving}
              className="bg-gradient-to-r from-blue-600 to-green-600"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Annotations ({annotations.length})
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <CardContent className="p-4 bg-gray-900">
          <div className="flex items-center justify-center">
            <Stage
              ref={stageRef}
              width={canvasWidth}
              height={canvasHeight}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              className="border-4 border-gray-300 rounded-lg shadow-2xl bg-white"
            >
              <Layer>
                {/* Background Image */}
                {image && (
                  <Image
                    image={image}
                    width={canvasWidth}
                    height={canvasHeight}
                  />
                )}

                {/* Annotations */}
                {renderAnnotations()}
              </Layer>
            </Stage>
          </div>

          {/* Instructions */}
          {annotations.length === 0 && (
            <div className="text-center mt-4 text-gray-400">
              <p className="text-sm">Select a tool above and draw on the satellite image</p>
              <p className="text-xs mt-1">Tap/click to draw • Drag for shapes • Use colors to highlight different areas</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inline Text Editor Overlay */}
      {isEditingText && textEditorPosition && (
        <div
          className="fixed z-[60]"
          style={{
            left: `${textEditorPosition.x}px`,
            top: `${textEditorPosition.y}px`,
          }}
        >
          <div className="bg-white border-2 border-blue-500 rounded-lg shadow-2xl p-2">
            <textarea
              ref={textInputRef}
              value={textEditorValue}
              onChange={(e) => setTextEditorValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  completeTextEditing();
                } else if (e.key === 'Escape') {
                  setIsEditingText(false);
                  setTextEditorPosition(null);
                  setTextEditorValue('');
                }
              }}
              onBlur={completeTextEditing}
              className="w-64 h-20 p-2 border-none outline-none resize-none text-lg font-bold"
              style={{ color: selectedColor }}
              placeholder="Type text here..."
              autoFocus
            />
            <div className="text-xs text-gray-500 mt-1 flex justify-between">
              <span>Enter to save • Esc to cancel</span>
              <span>Drag after saving</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
